<template>
  <form
    class="multi-store-modal container"
    :class="`multi-store-modal--${template}`"
    :action="action"
    method="post"
    @submit.prevent="handleLocalizationFormSubmit"
  >
    <div class="grid">
      <input
        name="_method"
        type="hidden"
        value="put"
      >

      <input
        name="return_to"
        type="hidden"
        :value="returnTo"
      >

      <!-- Options -->
      <template v-if="template === 'options'">
        <p
          class="
            multi-store-modal__options-title
            text-heading-xs
            text-heading-s-desktop
            col xs6 l2 xl4
          "
          v-text="$string('general.multi_store.options.title')"
        />

        <multi-store-options
          class="multi-store-modal__options-selects col xs6 l3-8 xl5-9"
          namespace="modal"
        />
      </template>

      <!-- Suggestions -->
      <div
        v-else
        class="multi-store-modal__suggestions col xs6 l6 xl5"
      >
        <p
          class="
            multi-store-modal__suggestions-title
            text-body-s
            text-body-m-desktop
          "
          v-html="suggestionStrings.title"
        />

        <ul
          class="
            multi-store-modal__suggestions-items
            text-body-xs
            text-body-s-desktop
          "
        >
          <li
            class="multi-store-modal__suggestions-item"
            v-text="suggestionStrings.currency"
          />

          <li
            class="multi-store-modal__suggestions-item"
            v-text="suggestionStrings.shipping"
          />

          <li
            class="multi-store-modal__suggestions-item"
            v-text="suggestionStrings.language"
          />
        </ul>

        <!-- Hidden inputs to set continue values -->
        <input
          name="country_code"
          type="hidden"
          :value="suggestedStore.country.code"
        >

        <input
          name="language_code"
          type="hidden"
          :value="suggestedStore.language.code"
        >
      </div>

      <!-- Footer -->
      <div
        class="multi-store-modal__footer col xs6"
        :class="template === 'options' ? 'l9-12 xl10-12' : 'l7-12 xl8-12'"
      >
        <btn
          class="multi-store-modal__continue"
          :label="$string('general.multi_store.continue')"
          modifiers="block"
          :show-icon="false"
          type="submit"
        />

        <btn
          class="multi-store-modal__alt-button"
          :label="altButtonString"
          label-class="text-body-s-link"
          modifiers="outlined block"
          :show-icon="false"
          type="button"
          @click.prevent="handleAltButtonClick"
        />
      </div>
    </div>

    <button
      class="
          overlay__close
          shelf__close
          multi-store-modal-overlay__close
          icon-button
        "
      type="button"
      @click.prevent="handleCloseOverlay"
    >
      <span
        class="visually-hidden"
        v-text="$string('accessibility.close')"
      />

      <icon-close />
    </button>

    <teleport to="body">
      <transition name="fade">
        <button
          v-if="isActive"
          class="window-overlay"
          :class="namespace"
          tabindex="-1"
          type="button"
          aria-hidden="true"
          @click.prevent="handleCloseOverlay"
        />
      </transition>
    </teleport>
  </form>
</template>

<script>

/**
* Vue: Multi-store modal (multi-store-modal)
* -----------------------------------------------------------------------------
* Modal to control multi-store settings.
*
* @param {Boolean} isActive - Modal is active.
*
*/
import { mapActions, mapState } from 'vuex'
import Btn from '~global/btn/btn'
import MultiStoreOptions from '~async/multi-store-options/multi-store-options'
import IconClose from '~icons/misc/close.svg'
import { multiStoreAction, multiStoreReturnTo } from '~/helpers/general'
export default {
  name: 'MultiStoreModal',
  components: {
    Btn,
    IconClose,
    MultiStoreOptions,
  },
  props: {
    isActive: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      namespace: 'multiStoreModal',
      template: 'suggestions',
    }
  },
  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      selectedStore: (state) => state['multi-store'].selectedStore,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
    }),

    /**
     * Compute active value.
     * - This is selected if user has changed select value, or suggested if user
     *   hasn't updated anything yet.
     */
    active() {
      return this.selectedStore.country ? this.selectedStore : this.suggestedStore
    },

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      return multiStoreAction(this.active?.instance)
    },

    /**
     * Compute alt button string.
     * @returns {String}
     */
    altButtonString() {
      return this.template === 'suggestions'
        ? this.$string('general.multi_store.suggestions.change')
        : this.$string('general.multi_store.options.cancel')
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      return multiStoreReturnTo(this.active)
    },

    /**
     * Compute suggestion strings.
     * @returns {Object}
     */
    suggestionStrings() {
      const currency = `${this.suggestedStore.country.currency} ${this.suggestedStore.country.currencySymbol}`
      return {
        currency: this.$string(
          'general.multi_store.suggestions.currency_html',
          { replace: { currency } },
        ),
        language: this.$string(
          'general.multi_store.suggestions.language_html',
          { replace: { language: this.suggestedStore.language.name } },
        ),
        shipping: this.$string(
          'general.multi_store.suggestions.shipping_html',
          { replace: { country: this.suggestedStore.country.name } },
        ),
        title: `
          ${this.$string('general.multi_store.suggestions.title')}
          <span class="text-heading-s text-heading-m-desktop">
            ${this.suggestedStore.country.name}
          </span>
        `,
      }
    },
  },
  watch: {

    /**
     * Watch template changing to set selected store back to suggested store.
     */
    template() {
      if (this.template !== 'suggestions') {
        return
      }
      this.setSelectedStore(this.suggestedStore)
    },
  },
  mounted() {
    cnvs.EventBus.emit('overlay:update', 'multiStoreModal')
  },
  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      setPreference: 'multi-store/setPreference',
      setSelectedStore: 'multi-store/setSelectedStore',
    }),

    /**
     * Handle localization form submit.
     * @param {Object} event - Submit event.
     */
    async handleLocalizationFormSubmit(event) {
      const form = event.target
      const formData = new FormData(form)
      this.setPreference({
        country: formData.get('country_code'),
        language: formData.get('language_code'),
      })

      /**
       * Wait for preferences to be set and action/returnTo computed props to
       * finish calculating.
       */
      await this.$nextTick()
      form.submit()
    },

    /**
     * Handle alt button click.
     */
    async handleAltButtonClick() {
      this.template = this.template === 'suggestions'
        ? 'options'
        : 'suggestions'
      await this.$nextTick()
      cnvs.EventBus.emit('overlay:update', this.namespace)
    },

    /**
     * Handle close overlay click.
     */
    handleCloseOverlay() {
      const data = {
        country: this.current.country.code,
        language: this.current.language.code,
      }
      sessionStorage.setItem('multi-store-dismissed', JSON.stringify(data))
      this.closeOverlay(this.namespace)
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-modal';
</style>
